import type { CitationReferenceData } from 'services/reference-generator/types';
import type { ReviewStore } from 'store/useReviewStore';
import { citationReviewStatus, isSuggestionMetadata, pickReviewCitation, reviewStatusOf } from './reviewAggregator';
import type { AIFieldMetadata } from '../components/captable-table/CaptableTable.props';

export type CitationReviewAction = 'accept' | 'dismiss' | 'reopen';

const STATUS_BY_ACTION: Record<CitationReviewAction, 'accepted' | 'dismissed' | 'pending'> = {
  accept: 'accepted',
  dismiss: 'dismissed',
  reopen: 'pending',
};

export const citationReviewStatusOf = (
  action: CitationReviewAction
): { status: 'accepted' | 'dismissed' | 'pending'; isAccepted: boolean } => ({
  status: STATUS_BY_ACTION[action],
  isAccepted: action === 'accept',
});

type CitationRef = { id?: number | null } | string | null;

export const applyCitationReviewToRefs = <T extends CitationRef>(
  refs: T[] | undefined,
  citationId: number,
  action: CitationReviewAction
): T[] | undefined => {
  if (!Array.isArray(refs)) {
    return refs;
  }
  const { status, isAccepted } = citationReviewStatusOf(action);
  return refs.map(ref =>
    typeof ref === 'object' && ref?.id === citationId ? { ...ref, status, is_accepted: isAccepted } : ref
  );
};

type CitationCell = {
  citationReferences?: CitationReferenceData[];
  aiFieldMetadata?: AIFieldMetadata | null;
  dispatchEvent?: () => void;
};

type CitationSecurity = {
  citation_references?: CitationRef[];
};

export const applyCitationReviewToCell = (
  cell: CitationCell | null | undefined,
  citationId: number,
  action: CitationReviewAction
) => {
  if (!cell || !Array.isArray(cell.citationReferences)) {
    return;
  }
  // The spreadsheet already holds this cell object; write the settled refs onto it.
  // eslint-disable-next-line no-param-reassign
  cell.citationReferences = applyCitationReviewToRefs(cell.citationReferences, citationId, action);
  cell.dispatchEvent?.();
};

export const applyCitationReviewToSecurities = (
  securities: CitationSecurity[] | undefined,
  citationId: number,
  action: CitationReviewAction
): void => {
  securities?.forEach(security => {
    if (security.citation_references?.some(ref => typeof ref === 'object' && ref?.id === citationId)) {
      // Keep the securities array stable so the grid does not re-parse.
      // eslint-disable-next-line no-param-reassign
      security.citation_references = applyCitationReviewToRefs(security.citation_references, citationId, action);
    }
  });
};

/** Immutable variant for flows that rebuild the securities array through a store patch. */
export const mapSecuritiesCitationReview = <T extends CitationSecurity>(
  securities: T[],
  citationId: number,
  action: CitationReviewAction
): T[] =>
    securities.map(security =>
      security.citation_references?.some(ref => typeof ref === 'object' && ref?.id === citationId)
        ? {
          ...security,
          citation_references: applyCitationReviewToRefs(security.citation_references, citationId, action),
        }
        : security
    );

export const reviewItemMatchingCitationId = <
  T extends { primaryCitation?: { id?: number | null }; citations?: { id?: number | null }[] },
>(
    items: T[],
    citationId: number
  ): T | undefined =>
    items.find(item => item.primaryCitation?.id === citationId || item.citations?.some(ref => ref.id === citationId));

type SettleCitationReviewParams = {
  citationId: number;
  action: CitationReviewAction;
  persist: (citationId: number, action: CitationReviewAction) => Promise<unknown>;
  getCell: () => CitationCell | null | undefined;
  getSecurities: () => CitationSecurity[] | undefined;
  getReviewStore: () => Pick<ReviewStore, 'reviewItems' | 'setReviewItems' | 'setReviewItemStatus'>;
  onSettled?: (itemId: string) => void;
};

/** Persist one citation decision without settling a field or rebuilding the grid. */
export const settleCitationReview = async ({
  citationId,
  action,
  persist,
  getCell,
  getSecurities,
  getReviewStore,
  onSettled,
}: SettleCitationReviewParams): Promise<string | undefined> => {
  await persist(citationId, action);
  const cell = getCell();
  applyCitationReviewToSecurities(getSecurities(), citationId, action);
  applyCitationReviewToCell(cell, citationId, action);
  const { reviewItems, setReviewItems, setReviewItemStatus } = getReviewStore();
  // The aggregator chooses citation review independently of settled value metadata.
  const item = reviewItemMatchingCitationId(
    reviewItems.filter(current => current.canSettleCitation && !current.canSettle),
    citationId
  );
  const fieldMetadata = cell?.aiFieldMetadata;
  if (!item || (isSuggestionMetadata(fieldMetadata) && reviewStatusOf(fieldMetadata) === 'pending')) {
    // A new value suggestion may have arrived while the citation request ran.
    return undefined;
  }
  const refs = cell?.citationReferences;
  if (!refs?.length) {
    return undefined;
  }
  // A sibling still awaiting review drives the card next. Field cards retain
  // their independent value decision, including their pending outline. The card
  // keeps the settled rows too, so its marker recolours with the grid badge.
  const primaryCitation = pickReviewCitation(refs);
  const status = citationReviewStatus(primaryCitation);
  setReviewItems(
    reviewItems.map(current => (current.id === item.id ? { ...current, primaryCitation, citations: refs } : current))
  );
  setReviewItemStatus(item.id, status);
  if (status !== 'pending') {
    onSettled?.(item.id);
  }
  return item.id;
};
